import { useEffect, useState } from 'react';
import Link from 'next/link';

import clsx from 'clsx';
import { filter, find } from 'lodash';

import useTranslation from '@/utils/hooks/translation/useTranslation';

import PropTypes from 'prop-types';

import { POST } from '@/lib/posts/postTypes';
import { FACEBOOK, INSTAGRAM, LINKEDIN, TIKTOK_BUSINESS, X } from '@/lib/socialProfile';

import Carousel from '@/components/base/Carousel';
import Icon from '@/components/base/Icon';
import Loader from '@/components/base/Loader';
import { PostComponent } from '@/components/base/Post/PostComponent';
import Select from '@/components/base/Select';
import SelectWithSPIcon from '@/components/base/Select/SelectWithSPIcon';
import Tooltip from '@/components/base/Tooltip';
import NoDataNetworksList from '@/components/composite/Analyze/NoDataNetworksList';
import Instagram from '@/components/icons/Instagram';

export const WidgetMedia = ({ children, ...restProps }) => {
    return (
        <div className="flex flex-col space-y-5" {...restProps}>
            {children}
        </div>
    );
};

WidgetMedia.propTypes = {
    children: PropTypes.node
};

const Header = ({ children }) => (
    <div className="flex items-center justify-between">{children}</div>
);

Header.propTypes = {
    children: PropTypes.node
};

WidgetMedia.Header = Header;

const CarouselWrapper = ({ children, navPosition, config, className, ...restProps }) => {
    return (
        <Carousel navPosition={navPosition} config={config} className={className} {...restProps}>
            {children}
        </Carousel>
    );
};

CarouselWrapper.propTypes = {
    children: PropTypes.node,
    navPosition: PropTypes.string,
    config: PropTypes.object,
    className: PropTypes.string
};

WidgetMedia.Carousel = CarouselWrapper;

const SelectSortBy = ({ data, network, setRequestParams, currentSort, onChange }) => {
    const [activeSort, setActiveSort] = useState(
        find(
            data,
            (item) => item.value === currentSort?.column && item.order === currentSort?.order
        ) ?? data?.[0]
    );
    useEffect(() => {
        if (data?.filter((el) => activeSort?.value === el?.value)?.length === 0) {
            setActiveSort(data[0]);
        }
    }, [activeSort, data]);

    useEffect(() => {
        setRequestParams((prevState) => ({
            ...prevState,
            sort: {
                column: activeSort?.value,
                order: activeSort?.order
            }
        }));
        if (typeof onChange === 'function') {
            onChange(activeSort);
        }
    }, [activeSort?.value, activeSort?.order]);

    if (data?.length < 2) {
        return (
            <NoSortTitle network={network} label={data?.[0]?.label_selected} className="ml-0.5" />
        );
    }

    return (
        <Select
            selected={
                filter(
                    data,
                    (element) =>
                        element?.value === activeSort?.value && element?.order === activeSort?.order
                )?.[0]
            }
            isTruncate={false}
            setSelected={setActiveSort}
            options={data}
            noBorder
            button={
                <SelectWithSPIcon
                    socialNetworkType={network}
                    value={activeSort?.label_selected ?? null}
                />
            }
        />
    );
};

SelectSortBy.propTypes = {
    data: PropTypes.array,
    setRequestParams: PropTypes.func,
    network: PropTypes.string,
    currentSort: PropTypes.object,
    onChange: PropTypes.func
};

WidgetMedia.SortBy = SelectSortBy;

const SelectType = ({ data, setRequestParams, currentFilter, onChange }) => {
    const [activeType, setActiveType] = useState(
        find(data, (item) => item.value === currentFilter?.[0]) ?? data?.[0]
    );

    //reset select type based on currentFilter & if currentFilter exist in the filter available
    useEffect(() => {
        if (data?.filter((el) => el?.value === activeType?.value)?.length === 0) {
            setActiveType(data[0]);
        }
    }, [activeType, data]);

    useEffect(() => {
        const filter_post_type = activeType?.value !== 'all' ? [activeType?.value] : [];
        setRequestParams((prevState) => ({
            ...prevState,
            filter_post_type
        }));
        if (typeof onChange === 'function') {
            onChange(filter_post_type);
        }
    }, [activeType?.value]);
    return (
        <Select
            selected={
                data?.filter((element) => element?.value === activeType?.value)?.[0] ?? data?.[0]
            }
            setSelected={setActiveType}
            options={data}
            noBorder
            buttonBaseStyle="noBorder"
        />
    );
};

SelectType.propTypes = {
    data: PropTypes.array,
    network: PropTypes.string,
    currentFilter: PropTypes.array,
    setRequestParams: PropTypes.func,
    onChange: PropTypes.func
};

WidgetMedia.SelectType = SelectType;

const ExportButton = ({ onClick, tooltipContent, isLoading }) => (
    <Tooltip content={tooltipContent}>
        <button
            disabled={isLoading}
            aria-label="xls-export"
            className="flex h-12 w-12 items-center justify-center rounded-full bg-white text-icoGray-500 dark:bg-icoDarkMode-anthracite dark:text-icoDarkMode-wolf"
            onClick={onClick}>
            {isLoading ? <Loader className="h-4 w-4" /> : <Icon icon="export" />}
        </button>
    </Tooltip>
);

ExportButton.propTypes = {
    onClick: PropTypes.func.isRequired,
    tooltipContent: PropTypes.string,
    isLoading: PropTypes.bool
};

WidgetMedia.ExportButton = ExportButton;

const NoData = ({ networks, message, isExport = false }) => (
    <NoDataNetworksList socialNetworkTypes={networks} isExport={isExport} className="min-h-[250px]">
        {message}
    </NoDataNetworksList>
);

NoData.propTypes = {
    networks: PropTypes.array,
    message: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    isExport: PropTypes.bool
};
WidgetMedia.NoData = NoData;

const ErrorFallBack = ({ resetErrorBoundary, isExport = false }) => {
    const { t } = useTranslation();
    return (
        <div
            role="alert"
            className={clsx(
                'flex min-h-[250px] w-full flex-1 flex-col items-center justify-center space-y-3 rounded bg-white p-8',
                { 'dark:bg-icoDarkMode-anthracite': !isExport, 'text-icoBlack': isExport }
            )}>
            <p className="text-center">{t('errors:error_occured_fetch_data')} </p>
            <button className="text-icoBlue underline" onClick={resetErrorBoundary}>
                {t('commons:try_again')}
            </button>
        </div>
    );
};

ErrorFallBack.propTypes = {
    resetErrorBoundary: PropTypes.func,
    isExport: PropTypes.bool
};

WidgetMedia.ErrorFallBack = ErrorFallBack;

const Placeholder = ({
    nbSlides = 10,
    postFormat,
    wrapperClass,
    postClass,
    placeholderConfig = {},
    isExport = false
}) => (
    <WidgetMedia.Carousel hasNavigation={false} className={wrapperClass}>
        {[...Array(nbSlides).keys()].map((idx) => (
            <Carousel.Slide aria-label={`media-loading-${idx}`} key={idx} className={postClass}>
                <PostComponent
                    network={placeholderConfig?.network}
                    type={placeholderConfig?.type}
                    config={{
                        postType: POST,
                        postFormat: postFormat,
                        activeFilter: null,
                        network: INSTAGRAM,
                        numberFormat: 'comma'
                    }}
                    isExport={isExport}
                    isLoading
                />
            </Carousel.Slide>
        ))}
    </WidgetMedia.Carousel>
);

Placeholder.propTypes = {
    nbSlides: PropTypes.number,
    postFormat: PropTypes.string,
    WrapperClass: PropTypes.string,
    postClass: PropTypes.string,
    placeholderConfig: PropTypes.object,
    isExport: PropTypes.bool
};

WidgetMedia.Placeholder = Placeholder;

const ShowMore = ({ url, textContent }) => {
    return (
        <Link href={url}>
            <>
                <div
                    className="flex h-full w-full items-center justify-center group-hover:cursor-pointer"
                    aria-label="show-more">
                    <span className="flex items-center dark:text-white">
                        {textContent}
                        <Icon
                            className="ml-2 text-sm text-icoGray-400 group-hover:text-icoBlue dark:text-icoDarkMode-wolf"
                            icon="chevron-right"
                        />
                    </span>
                </div>
            </>
        </Link>
    );
};

ShowMore.propTypes = {
    url: PropTypes.string.isRequired,
    textContent: PropTypes.string.isRequired
};

WidgetMedia.ShowMore = ShowMore;

const NoSortTitle = ({ network, label, className }) => (
    <div className={clsx('flex items-center space-x-2.5 overflow-hidden', className)}>
        {network ? (
            network === INSTAGRAM ? (
                <Instagram size={16} aria-label="instagram" />
            ) : (
                <Icon
                    icon={network}
                    className={clsx({
                        'text-facebook dark:text-white': network === FACEBOOK,
                        'text-linkedin dark:text-white': network === LINKEDIN,
                        'text-twitter dark:text-white': network === X,
                        'text-black dark:text-white': network === TIKTOK_BUSINESS
                    })}
                />
            )
        ) : null}
        <p className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-lg">{label}</p>
    </div>
);

NoSortTitle.propTypes = {
    network: PropTypes.string,
    label: PropTypes.string,
    className: PropTypes.string
};
